import { useToast } from '@welbe/web-design-system';
import HappyFaceSvg from 'App/assets/img/happy-face.svg?url';
import { BrokersContext } from 'contexts/Brokers/Brokers';
import Analytics from 'helpers/analytics';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useSessionStorage } from 'hooks/useSessionStorage';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Candidate } from 'types';
import { getKey } from 'utils/getKey';
import { getCsvCandidate } from './components/CandidateForm/services';

const initialStateCofirmModal = {
	show: false,
	message: '',
	icon: '',
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onClose: () => {},
};

const useCandidates = () => {
	const { toast } = useToast();
	const { getSessionStorage } = useSessionStorage();
	const { getLocalStorage, setLocalStorage } = useLocalStorage();
	const { isBroker } = useContext(BrokersContext);
	const [width, setWidth] = useState<string>('auto');
	const aside: any = document.querySelector('aside');
	if (aside) {
		const observer = new MutationObserver(() => {
			getContentWidth();
		});

		observer.observe(aside, {
			attributes: true,
		});
	}
	const navigate = useNavigate();
	const [selectedCandidates, setSelectedCandidates] = React.useState<
		Candidate[]
	>([]);
	const [isProfileButtonVisible, setProfileButtonVisible] = useState(false);
	const [isOpenDrawer, setIsOpenDrawer] = useState(false);
	const [isProfileModalVisible, setProfileModalVisible] = useState(false);
	const [isOpenModalUpload, setOpenModalUpload] = useState(false);
	const [reloadTable, setReloadTable] = useState(false);
	const [showTutorial, setShowTutorial] = useState(false);
	const [isDisabledFeatModalVisible, setDisabledFeatModalVisible] =
		useState(false);
	const [showModalRejectCandidate, setShowModalRejectCandidate] =
		useState(false);
	const [showModalApproveCandidate, setShowModalApproveCandidate] =
		useState(false);
	const [showCallToActionCandidateModal, setShowCallToActionCandidateModal] =
		useState(false);
	const [showCallToActionExamsModal, setShowCallToActionExamsModal] =
		useState(false);
	const [isLoadingCsv, setLoadingCsv] = useState<boolean>(false);
	const [confirmModal, setConfirmModal] = useState(initialStateCofirmModal);

	const handleNewCandidate = () => {
		const meInfo = JSON.parse(getSessionStorage('me') || '{}');

		const createCandidateFlag =
			meInfo?.company?.companyPortalSettings?.candidates?.createCandidate;
		const isCandidateFeatDisabled = createCandidateFlag === false;

		if (isCandidateFeatDisabled) {
			setDisabledFeatModalVisible(true);
		} else {
			Analytics.track('candidate', 'Initiated Create Candidate', {
				isBroker,
			});
			navigate('/candidates/new');
		}
	};

	const handleCloseShowCallToActionCandidateModal = () => {
		setShowCallToActionCandidateModal(false);
	};

	const handleShowModalApproveCandidate = () => {
		setShowModalApproveCandidate(true);
	};

	const handleCloseModalApproveCandidate = () => {
		setShowModalApproveCandidate(false);
	};

	const handleCloseShowCallToActionExamsModal = () => {
		setShowCallToActionExamsModal(false);
	};

	const handleSelectCandidates = (candidates: any) => {
		const updatedCandidates = candidates.map((candidate: any) => ({
			...candidate,
			results: getKey(candidate.results),
			certifiedFile: getKey(candidate.certifiedFile),
			statusRH: getKey(candidate.statusRH),
			name: getKey(candidate.name),
			serviceRequest: getKey(candidate.serviceRequest),
			statusDoctor: getKey(candidate.statusDoctor),
		}));

		setSelectedCandidates(updatedCandidates);
	};

	const handleDownloadCsv = async () => {
		try {
			setLoadingCsv(true);
			await getCsvCandidate();
			Analytics.track('candidate', 'Download CSV', {
				isBroker,
			});
			setConfirmModal({
				show: true,
				message:
					'El Informe de candidatos esta siendo generado, e llegara en tu correo en instantes',
				icon: HappyFaceSvg,
				onClose: () => {
					setConfirmModal({
						...confirmModal,
						show: false,
					});
				},
			});
			return;
		} catch (error) {
			Analytics.track('candidate', 'Error Download CSV', {
				isBroker,
			});
			toast.error('Error al enviar CSV.');
		} finally {
			setLoadingCsv(false);
		}
	};

	const handleToggleProfileModal = () => {
		if (!isProfileButtonVisible) {
			setShowCallToActionExamsModal(true);
			return;
		}
		setProfileModalVisible((prev) => !prev);
	};

	const handleApproveCandidates = () => {
		handleCloseModalApproveCandidate();
		setSelectedCandidates([]);
	};

	const handleShowModalRejectCandidate = () => {
		setShowModalRejectCandidate(true);
	};

	const handleCloseModalRejectCandidate = () => {
		setShowModalRejectCandidate(false);
	};

	const handleEditAppointment = () => {
		const [candidate] = selectedCandidates;
		navigate(`/candidates/update/${candidate.id}/appointment`);
	};

	const handleCSV = () => {
		setOpenModalUpload(true);
	};

	const handleCloseModalUpload = () => {
		setOpenModalUpload(false);
	};

	const hasJustPendentAtRHStatus = selectedCandidates.length
		? selectedCandidates.every(
				(candidates: any) => candidates.statusRH === 'pendent',
		  )
		: false;

	const hasJustPendentAtDoctorStatus = selectedCandidates.length
		? selectedCandidates.some(
				(candidates: any) =>
					candidates.statusDoctor === 'pendent' ||
					candidates.statusDoctor === 'appointmentRequested',
		  )
		: false;

	const getContentWidth = () => {
		const element: any = document.getElementById('container-candidates');
		setWidth(!element ? '90%' : `${element.getBoundingClientRect().width}px`);
	};

	const handleOpenCandidateTutorial = () => {
		setShowTutorial(true);
	};

	const handleFinishCandidateTutorial = () => {
		setLocalStorage('candidateTutorial', 'true');
		if (!isProfileButtonVisible) {
			setShowCallToActionCandidateModal(true);
		}
		setShowTutorial(false);
	};

	useEffect(() => {
		setIsOpenDrawer(Boolean(selectedCandidates.length));
	}, [selectedCandidates]);

	useEffect(() => {
		const currentUser = JSON.parse(getSessionStorage('me') || '{}');
		const showedTutorial = Boolean(
			getLocalStorage('candidateTutorial') || false,
		);
		if (!showedTutorial) {
			handleOpenCandidateTutorial();
		}
		setProfileButtonVisible(
			Boolean(
				currentUser?.company?.companyPortalSettings?.candidates
					?.showCandidateAppointmentProfile,
			),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		getContentWidth();
	}, []);

	return {
		isDisabledFeatModalVisible,
		setDisabledFeatModalVisible,
		selectedCandidates,
		isOpenDrawer,
		isOpenModalUpload,
		isLoadingCsv,
		reloadTable,
		showModalApproveCandidate,
		showModalRejectCandidate,
		hasJustPendentAtRHStatus,
		hasJustPendentAtDoctorStatus,
		isBroker,
		width,
		isProfileButtonVisible,
		isProfileModalVisible,
		showTutorial,
		showCallToActionCandidateModal,
		showCallToActionExamsModal,
		handleCSV,
		setReloadTable,
		setIsOpenDrawer,
		handleToggleProfileModal,
		handleEditAppointment,
		handleNewCandidate,
		setSelectedCandidates,
		handleCloseModalUpload,
		handleSelectCandidates,
		handleApproveCandidates,
		handleShowModalRejectCandidate,
		handleCloseModalRejectCandidate,
		handleShowModalApproveCandidate,
		handleCloseModalApproveCandidate,
		handleFinishCandidateTutorial,
		handleCloseShowCallToActionCandidateModal,
		handleCloseShowCallToActionExamsModal,
		handleDownloadCsv,
		confirmModal,
		setConfirmModal,
	};
};

export default useCandidates;
